/**
 * useEntitlements Hook
 * Custom hook for managing platform modules and entitlements
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { modulesService, type Module, type CompanyEntitlements } from '@/services/api/modulesService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useCallback, useMemo } from 'react';

/**
 * Hook para obtener todos los módulos disponibles
 */
export function useModules(params?: {
  category?: string;
  min_subscription_tier?: string;
  is_free?: boolean;
  is_beta?: boolean;
}) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['modules', params],
    queryFn: () => modulesService.getModules(params),
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes - modules don't change often
  });
}

/**
 * Hook para obtener módulos agrupados por categoría
 */
export function useModulesByCategory() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['modules', 'by-category'],
    queryFn: () => modulesService.getModulesByCategory(),
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook para obtener los entitlements del usuario actual
 * Este es el hook principal para verificar qué módulos puede usar el usuario
 */
export function useMyEntitlements() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['entitlements', 'me'],
    queryFn: () => modulesService.getMyEntitlements(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para verificar si el usuario puede usar módulo(s) específico(s)
 * Returns a function that checks module access from cached entitlements
 */
export function useCanUseModule() {
  const { data: entitlements, isLoading } = useMyEntitlements();

  const canUse = useCallback(
    (moduleCode: string): boolean => {
      if (!entitlements) return false;
      return entitlements.enabled_modules.includes(moduleCode);
    },
    [entitlements]
  );

  const canUseAny = useCallback(
    (moduleCodes: string[]): boolean => {
      if (!entitlements) return false;
      return moduleCodes.some((code) => entitlements.enabled_modules.includes(code));
    },
    [entitlements]
  );

  const canUseAll = useCallback(
    (moduleCodes: string[]): boolean => {
      if (!entitlements) return false;
      return moduleCodes.every((code) => entitlements.enabled_modules.includes(code));
    },
    [entitlements]
  );

  return {
    canUse,
    canUseAny,
    canUseAll,
    isLoading,
    enabledModules: entitlements?.enabled_modules ?? [],
    isPlatform: entitlements?.is_platform ?? false,
    plan: entitlements?.plan ?? 'free',
  };
}

/**
 * Hook para obtener entitlements de una compañía específica (admin)
 */
export function useCompanyEntitlements(companyId: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['entitlements', 'company', companyId],
    queryFn: () => modulesService.getCompanyEntitlements(companyId!),
    enabled: isAuthenticated && !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para grant/revoke modules (admin)
 */
export function useManageCompanyModules() {
  const queryClient = useQueryClient();

  const grantModule = useMutation({
    mutationFn: ({
      companyId,
      moduleCode,
      options,
    }: {
      companyId: string;
      moduleCode: string;
      options?: { usage_limit?: number; custom_price?: number; notes?: string };
    }) => modulesService.grantModule(companyId, moduleCode, options),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entitlements', 'company', variables.companyId] });
    },
  });

  const revokeModule = useMutation({
    mutationFn: ({ companyId, moduleCode }: { companyId: string; moduleCode: string }) =>
      modulesService.revokeModule(companyId, moduleCode),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entitlements', 'company', variables.companyId] });
    },
  });

  return { grantModule, revokeModule };
}

/**
 * Hook combinado para entitlements y módulos con helpers
 */
export function useEntitlements() {
  const { data: entitlements, isLoading: isLoadingEntitlements } = useMyEntitlements();
  const { data: modules, isLoading: isLoadingModules } = useModules();

  // Memoize enabled module codes set for faster lookups
  const enabledModulesSet = useMemo(
    () => new Set(entitlements?.enabled_modules ?? []),
    [entitlements?.enabled_modules]
  );

  // Get modules that are enabled for this company
  const enabledModules = useMemo(() => {
    if (!modules) return [];
    return modules.filter((m) => enabledModulesSet.has(m.code));
  }, [modules, enabledModulesSet]);

  // Get modules that are NOT enabled (for upsell)
  const disabledModules = useMemo(() => {
    if (!modules) return [];
    return modules.filter((m) => !enabledModulesSet.has(m.code));
  }, [modules, enabledModulesSet]);

  // Check if module is enabled
  const isModuleEnabled = useCallback(
    (moduleCode: string): boolean => enabledModulesSet.has(moduleCode),
    [enabledModulesSet]
  );

  // Group enabled modules by category
  const enabledByCategory = useMemo(() => {
    return entitlements?.modules_by_category ?? {};
  }, [entitlements?.modules_by_category]);

  return {
    // Data
    entitlements,
    modules,
    enabledModules,
    disabledModules,
    enabledByCategory,

    // Helpers
    isModuleEnabled,
    isPlatform: entitlements?.is_platform ?? false,
    plan: entitlements?.plan ?? 'free',
    companyName: entitlements?.company_name ?? '',

    // Loading states
    isLoading: isLoadingEntitlements || isLoadingModules,
    isLoadingEntitlements,
    isLoadingModules,
  };
}

// Re-export types
export type { Module, CompanyEntitlements };
